import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "./api";
import { useAuth } from "./auth";
import {
  DEFAULT_HOST_THEME,
  hostThemeMeta,
  nextHostTheme,
  normalizeHostTheme,
  readPreviewHostTheme,
  writePreviewHostTheme,
} from "./hostThemes";

const HostThemePreviewContext = createContext(null);

export function HostThemePreviewProvider({ children }) {
  const { account, updateAccount } = useAuth();
  const [previewTheme, setPreviewTheme] = useState(() => {
    if (account?.theme) return normalizeHostTheme(account.theme);
    return readPreviewHostTheme();
  });
  const [saving, setSaving] = useState(false);

  // Keep preview in sync when account loads / theme saved elsewhere (Host → Theme).
  const accountTheme = account ? normalizeHostTheme(account.theme) : null;
  const themeId = accountTheme || previewTheme || DEFAULT_HOST_THEME;

  const applyLocal = useCallback(
    (next) => {
      const id = normalizeHostTheme(next);
      setPreviewTheme(id);
      writePreviewHostTheme(id);
      return id;
    },
    [],
  );

  const setTheme = useCallback(
    async (next, { toastName = true } = {}) => {
      const id = applyLocal(next);
      if (!account) {
        if (toastName) toast.message(hostThemeMeta(id).name);
        return id;
      }
      setSaving(true);
      try {
        const { data } = await api.patch("/organizers/me", { theme: id });
        if (data?.organizer) updateAccount(data.organizer);
        if (toastName) toast.success(hostThemeMeta(id).name);
        return id;
      } catch (err) {
        toast.error(formatApiError(err) || "Couldn’t save theme");
        return themeId;
      } finally {
        setSaving(false);
      }
    },
    [account, applyLocal, themeId, updateAccount],
  );

  const cycleTheme = useCallback(() => setTheme(nextHostTheme(themeId)), [setTheme, themeId]);

  // When signed-in account theme changes (e.g. Host → Theme), mirror locally.
  React.useEffect(() => {
    if (accountTheme) setPreviewTheme(accountTheme);
  }, [accountTheme]);

  const value = useMemo(
    () => ({
      themeId,
      setTheme,
      cycleTheme,
      saving,
      canCycle: true,
    }),
    [themeId, setTheme, cycleTheme, saving],
  );

  return (
    <HostThemePreviewContext.Provider value={value}>
      {children}
    </HostThemePreviewContext.Provider>
  );
}

export function useHostThemePreview() {
  const ctx = useContext(HostThemePreviewContext);
  if (!ctx) {
    return {
      themeId: DEFAULT_HOST_THEME,
      setTheme: async () => DEFAULT_HOST_THEME,
      cycleTheme: () => {},
      saving: false,
      canCycle: false,
    };
  }
  return ctx;
}

/** Product chrome wrapper — Home / feed preview theme (not others’ public pages). */
export function HostThemePreviewShell({ className = "", children }) {
  const { themeId } = useHostThemePreview();
  return (
    <div data-host-theme={normalizeHostTheme(themeId)} className={className}>
      {children}
    </div>
  );
}
